import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";

const belongsToShop = (record, shopId) => {
  if (!shopId) return true;
  const recordShopId =
    record?.shop ??
    record?.shop_id ??
    record?.shop_details?.id ??
    record?.shop_data?.id;
  return recordShopId != null && String(recordShopId) === String(shopId);
};

const getStockProduct = (stock) =>
  stock?.product_details || stock?.purchase_item_details?.product || null;

const getStockUnit = (stock) =>
  stock?.unit_details || stock?.purchase_item_details?.unit_details || null;

const getStockOptionLabel = (stock) => {
  const product = getStockProduct(stock);
  const unit = getStockUnit(stock);
  return [product?.name, unit?.title].filter(Boolean).join(" - ");
};

const getItemTotal = (item) => {
  const quantity = parseFloat(item?.quantity) || 0;
  const salePrice = parseFloat(item?.sale_price) || 0;
  return quantity * salePrice;
};

const SaleAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleAdd, loading, resetForm } = useAdd("shop/sales", token);

  // Fetch dropdown data for shops, customers, units, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop", "nopagination", "v2"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: customers, loading: customerLoading } = useFetchObjects(
    ["customer", "nopagination", "v2"],
    "shop/customers/?page_size=all",
    token
  );
  const { data: units, loading: unitLoading } = useFetchObjects(
    ["unit", "nopagination", "v2"],
    "shop/units/?page_size=all",
    token
  );
  const { data: currencies, loading: currencyLoading } = useFetchObjects(
    ["currency", "nopagination", "v2"],
    "shop/currencies/?page_size=all",
    token
  );
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(
    ["employees", "sale", "add"],
    "shop/employees/?page_size=all",
    token
  );

  // Fetch all stocks (no shop filter) so all purchase items are always available.
  const [shop, setShop] = useState("");
  const [stocks, setStocks] = useState([]);
  const {
    data: stocksData,
    loading: stockLoading,
    refetch: refetchStocks,
  } = useFetchObjects(
    ["stock", "sale", "nopagination", "v2"],
    "shop/stocks/?page_size=all",
    token
  );

  useEffect(() => {
    if (stocksData) {
      const stocksList = Array.isArray(stocksData) ? stocksData : (stocksData?.results || []);
      setStocks(stocksList);
    } else {
      setStocks([]);
    }
  }, [stocksData]);

  useEffect(() => {
    if (open) {
      refetchStocks();
    }
  }, [open, refetchStocks]);

  const shopsList = Array.isArray(shopsData) ? shopsData : (shopsData?.results || []);
  const hasSingleShop = shopsList.length === 1;
  const customersList = Array.isArray(customers) ? customers : (customers?.results || []);
  const unitsList = Array.isArray(units) ? units : (units?.results || []);
  const employeesList = Array.isArray(employeesData)
    ? employeesData
    : (employeesData?.results || []);

  const branchCustomers = useMemo(
    () => customersList.filter((customer) => belongsToShop(customer, shop)),
    [customersList, shop]
  );
  const branchUnits = useMemo(
    () => unitsList.filter((unit) => belongsToShop(unit, shop)),
    [unitsList, shop]
  );
  const branchCurrencies = useMemo(
    () =>
      getUniqueCurrencies(
        (Array.isArray(currencies) ? currencies : currencies?.results || []).filter(
          (currency) => belongsToShop(currency, shop)
        )
      ),
    [currencies, shop]
  );
  const branchEmployees = useMemo(
    () => employeesList.filter((employee) => belongsToShop(employee, shop)),
    [employeesList, shop]
  );

  // Auto-select shop if only one exists.
  useEffect(() => {
    if (shopsList.length === 1 && !shop) {
      setShop(String(shopsList[0].id));
    }
  }, [shopsList, shop]);

  const availableStocks = useMemo(() => {
    const stocksList = Array.isArray(stocks) ? stocks : [];
    return shop
      ? stocksList.filter((s) => String(s.shop) === String(shop))
      : stocksList;
  }, [stocks, shop]);

  // Local state for sale-level fields.
  const [customerSelected, setCustomerSelected] = useState("");
  const [saleDate, setSaleDate] = useState(""); // date only
  const [saleCurrency, setSaleCurrency] = useState(""); // sale-level currency
  const [discount, setDiscount] = useState("");
  const [rent, setRent] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [priceType, setPriceType] = useState("retail"); // "wholesale" | "retail"
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});

  const handleShopChange = (value) => {
    setShop(value);
    setCustomerSelected("");
    setSaleCurrency("");
    setSelectedEmployeeId("");
    setItems([]);
    setErrors({});
  };

  // Add a new sale item row.
  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        stock_record_id: "",
        unit: "",
        available: "",
        quantity: "",
        sale_price: "",
        buy_price: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  // Remove an item row.
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // When a product is selected from Autocomplete, auto-fill unit, sale_price, and available quantity.
  const handleProductSelect = (index, selectedStock) => {
    const newItems = [...items];
    newItems[index].stock_record_id = selectedStock ? String(selectedStock.id) : "";
    newItems[index].product = selectedStock
      ? String(getStockProduct(selectedStock)?.id || "")
      : "";
    if (selectedStock) {
      const stockRecord = selectedStock;
      if (stockRecord) {
        const bp = stockRecord.buy_price || stockRecord.purchase_item_details?.buy_price || "";
        const wp = stockRecord.wholesale_price || stockRecord.purchase_item_details?.wholesale_price || "";
        const rp = stockRecord.retail_price
          || stockRecord.purchase_item_details?.retail_price
          || stockRecord.purchase_item_details?.sale_price
          || stockRecord.sale_price || "";

        // Auto-fill unit from the stock record
        const unitDetails = getStockUnit(stockRecord);
        newItems[index].unit = unitDetails ? String(unitDetails.id) : "";
        newItems[index].available = stockRecord.quantity;
        newItems[index].buy_price = bp;
        newItems[index].wholesale_price = wp;
        newItems[index].retail_price = rp;

        // Auto-fill sale_price based on the global priceType selector
        if (priceType === "wholesale") {
          newItems[index].sale_price = wp || rp || stockRecord.sale_price || "";
        } else {
          // retail (default)
          newItems[index].sale_price = rp || stockRecord.sale_price || "";
        }
      } else {
        newItems[index].unit = "";
        newItems[index].sale_price = "";
        newItems[index].available = "0";
        newItems[index].buy_price = "";
        newItems[index].wholesale_price = "";
        newItems[index].retail_price = "";
      }
    } else {
      newItems[index].stock_record_id = "";
      newItems[index].unit = "";
      newItems[index].sale_price = "";
      newItems[index].available = "0";
      newItems[index].buy_price = "";
      newItems[index].wholesale_price = "";
      newItems[index].retail_price = "";
    }
    setItems(newItems);
  };

  // When the global price type changes, re-apply to all items that already have a product
  const handlePriceTypeChange = (newType) => {
    setPriceType(newType);
    setItems((prev) =>
      prev.map((item) => {
        if (!item.product) return item;
        if (newType === "wholesale") {
          return { ...item, sale_price: item.wholesale_price || item.retail_price || item.sale_price };
        }
        // retail
        return { ...item, sale_price: item.retail_price || item.sale_price };
      })
    );
  };

  // Update a sale item field.
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Calculate total sale amount.
  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.sale_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const rentValue = parseFloat(rent) || 0;
  const finalTotal = totalAmount - discountValue - rentValue;

  // Validate form fields.
  const validate = () => {
    let newErrors = {};
    if (!shop) newErrors.shop = t("sale.shopRequired") || "Branch is required";
    if (!customerSelected)
      newErrors.customer = t("sale.customerRequired") || "Customer is required";
    if (!saleDate)
      newErrors.saleDate =
        t("sale.saleDateRequired") || "Sale date is required";
    if (!saleCurrency)
      newErrors.currency = t("sale.currencyRequired") || "Currency is required";
    if (items.length === 0) {
      newErrors.items =
        t("sale.atLeastOneItemRequired") ||
        "At least one sale item is required";
    } else {
      items.forEach((item, idx) => {
        if (!item.product)
          newErrors[`item_${idx}_product`] =
            t("sale.productRequired") || "Product is required";
        if (!item.unit)
          newErrors[`item_${idx}_unit`] =
            t("sale.unitRequired") || "Unit is required";
        if (!item.quantity)
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityRequired") || "Quantity is required";
        else if (parseFloat(item.quantity) > parseFloat(item.available || "0"))
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityExceedsAvailable") ||
            "Quantity exceeds available stock";
        if (!item.sale_price)
          newErrors[`item_${idx}_salePrice`] =
            t("sale.salePriceRequired") || "Sale price is required";
      });
    }
    if (receivedAmount !== "" && parseFloat(receivedAmount) > finalTotal) {
      newErrors.receivedAmount =
        t("sale.receivedExceedsTotal") ||
        "Received amount should not exceed total amount";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      shop,
      customer: customerSelected,
      amount: discountValue,
      rent: rentValue,
      received_amount: receivedAmount,
      sale_date: saleDate,
      currency: saleCurrency,
      notes: notes.trim() || undefined,
      items,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setShop(hasSingleShop ? String(shopsList[0].id) : "");
      setCustomerSelected("");
      setDiscount("");
      setRent("");
      setSelectedEmployeeId("");
      setReceivedAmount("");
      setNotes("");
      setPriceType("retail");
      setSaleDate("");
      setSaleCurrency("");
      setItems([]);
      setErrors({});
      setFormError("");
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, shopsList]);

  const getEmployeeName = (employee) => {
    if (!employee) return "";
    if (employee.first_name && employee.last_name) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return employee.username || employee.name || "";
  };

  // Get unit title from ID
  const getUnitTitle = (unitId) => {
    const unit = branchUnits.find(u => String(u.id) === String(unitId));
    return unit ? unit.title : "";
  };

  // Format product with size only (without unit)
  const formatProductWithSize = (product) => {
    if (!product) return "";
    let displayName = product.name || "";
    if (product.size) {
      displayName += ` - ${product.size}`;
    }
    return displayName;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir={fieldDirection}>
      <DialogTitle>{t("sale.addSale")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2, textAlign: fieldTextAlign }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2, direction: fieldDirection }}
        >
          {/* Sale-level fields arranged in two columns */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectShop")}
                fullWidth
                required
                value={shop}
                onChange={(e) => handleShopChange(e.target.value)}
                disabled={shopLoading}
                error={Boolean(errors.shop)}
                helperText={errors.shop}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                {shopsList.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={branchCustomers}
                getOptionLabel={(option) => option.name || ""}
                getOptionKey={(option) => option.id}
                value={branchCustomers.find((c) => String(c.id) === customerSelected) || null}
                onChange={(event, newValue) => setCustomerSelected(newValue ? String(newValue.id) : "")}
                loading={customerLoading}
                disabled={!shop || customerLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("sale.selectCustomer")}
                    required
                    error={Boolean(errors.customer)}
                    helperText={errors.customer}
                    {...getRtlTextFieldProps(formDirection, {
                      inputProps: params.inputProps,
                      InputLabelProps: params.InputLabelProps,
                    })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <JalaliDateField mode="date"
                label={t("sale.saleDate")}
                fullWidth
                required
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                error={Boolean(errors.saleDate)}
                helperText={errors.saleDate}
                {...getRtlTextFieldProps(formDirection, { InputLabelProps: { shrink: true } })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.currency")}
                fullWidth
                required
                value={saleCurrency}
                onChange={(e) => setSaleCurrency(e.target.value)}
                disabled={!shop || currencyLoading}
                error={Boolean(errors.currency)}
                helperText={errors.currency}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                {branchCurrencies.map((curr) => (
                  <MenuItem key={curr.id} value={String(curr.id)}>
                    {curr.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("Discount-Amount")}
                type="number"
                fullWidth
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                error={Boolean(errors.discount)}
                helperText={errors.discount}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.rent", "Rent")}
                type="number"
                fullWidth
                value={rent}
                onChange={(e) => setRent(e.target.value)}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectEmployee", "Select Employee")}
                fullWidth
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                disabled={!shop || employeeLoading}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                <MenuItem value="">
                  {t("sale.selectEmployee", "Select Employee")}
                </MenuItem>
                {branchEmployees.map((employee) => (
                  <MenuItem key={employee.id} value={String(employee.id)}>
                    {getEmployeeName(employee)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("sale.note")}
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("sale.notePlaceholder")}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
            {/* Price Type Selector */}
            <Grid item xs={12}>
              <TextField
                select
                label={t("sale.priceType", "Price Type")}
                fullWidth
                value={priceType}
                onChange={(e) => handlePriceTypeChange(e.target.value)}
                helperText={t(
                  "sale.priceTypeHelper",
                  "Choosing a price type auto-fills the sale price for every item you add"
                )}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                <MenuItem value="retail">
                  🛒 {t("purchase.retailPrice", "Retail Price")}
                </MenuItem>
                <MenuItem value="wholesale">
                  🏷️ {t("purchase.wholesalePrice", "Wholesale Price")}
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {/* Sale Items Section */}
          <Box sx={{ mt: 2, direction: fieldDirection }}>
            <Typography variant="h6" sx={{ textAlign: fieldTextAlign }}>{t("sale.saleItems")}</Typography>
            {errors.items && (
              <Typography color="error" variant="body2" sx={{ textAlign: fieldTextAlign }}>
                {errors.items}
              </Typography>
            )}
            {items.map((item, index) => {
              const itemTotal = getItemTotal(item);
              const selectedIds = items.map((i) => i.stock_record_id);
              const filteredOptions = Array.isArray(availableStocks) 
                ? availableStocks.filter(
                    (stock) =>
                      String(stock.id) === item.stock_record_id ||
                      !selectedIds.includes(String(stock.id))
                  )
                : [];
              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                    direction: fieldDirection,
                  }}
                >
                  {/* Row 1: Autocomplete product select and Remove icon */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Autocomplete
                      options={filteredOptions}
                      getOptionLabel={(option) => getStockOptionLabel(option)}
                      getOptionKey={(option) => option.id}
                      value={
                        filteredOptions.find(
                          (stock) => String(stock.id) === item.stock_record_id
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleProductSelect(index, newValue ? newValue : null)
                      }
                      disabled={!shop || stockLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("sale.selectProduct")}
                          required
                          error={Boolean(errors[`item_${index}_product`])}
                          helperText={errors[`item_${index}_product`]}
                          {...getRtlTextFieldProps(formDirection, {
                            inputProps: params.inputProps,
                            InputLabelProps: params.InputLabelProps,
                          })}
                        />
                      )}
                      sx={{ flex: 2, direction: fieldDirection }}
                    />
                    <IconButton onClick={() => removeItem(index)} color="error">
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                  {/* Row 2: All sale item fields in one row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, direction: fieldDirection }}>
                    <TextField
                      label={t("sale.availableQuantity")}
                      value={item.available || ""}
                      InputProps={{ readOnly: true }}
                      {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                    />
                    <TextField
                      label={t("sale.quantity")}
                      type="number"
                      required
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      error={Boolean(errors[`item_${index}_quantity`])}
                      helperText={errors[`item_${index}_quantity`]}
                      {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                    />
                    {/* Unit field - Auto-filled and READ-ONLY */}
                    <TextField
                      label={t("sale.selectUnit")}
                      required
                      value={getUnitTitle(item.unit)}
                      InputProps={{ readOnly: true }}
                      error={Boolean(errors[`item_${index}_unit`])}
                      helperText={errors[`item_${index}_unit`]}
                      {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                    />
                    <TextField
                      label={t("sale.salePrice")}
                      type="number"
                      required
                      value={item.sale_price}
                      onChange={(e) =>
                        handleItemChange(index, "sale_price", e.target.value)
                      }
                      error={Boolean(errors[`item_${index}_salePrice`])}
                      helperText={errors[`item_${index}_salePrice`]}
                      {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                    />
                    <TextField
                      label={t("sale.total", "Total")}
                      value={itemTotal.toFixed(2)}
                      InputProps={{ readOnly: true }}
                      {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                    />
                  </Box>
                </Box>
              );
            })}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              disabled={!shop}
              sx={{ mt: 1 }}
            >
              {t("sale.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2, textAlign: fieldTextAlign }}>
            <Typography variant="body1">
              {t("sale.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("sale.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            {rentValue > 0 && (
              <Typography variant="body1">
                {t("sale.rent", "Rent")}: -{rentValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("sale.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>

            <Grid item xs={6}>
              {/* <TextField
                label={t("sale.receivedAmount")}
                type="number"
                fullWidth
                value={receivedAmount}
                onChange={(e) => setReceivedAmount(e.target.value)}
                error={Boolean(errors.receivedAmount)}
                helperText={errors.receivedAmount}
              /> */}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("sale.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("sale.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleAdd;
